import { useMemo, useState } from 'react'

import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import type {
  CategoryRow,
  DailyVolumeRow,
  PostcodeHotspotRow,
  SeverityTrendRow,
  TrendsAnalytics,
} from '../hooks/useAnalytics'
import { useFTFAnalytics, useTrendsAnalytics } from '../hooks/useAnalytics'
import { tokens } from '../theme'

// Severity / category colours used across every chart. Single map so a
// legend change lands once and every widget picks it up.
const sevColour = {
  critical: tokens.alert,
  high: '#E09935',
  warn: tokens.gold,
  info: tokens.textSec,
} as const

// Category palette — lifted from the brand indigo family with accent
// highlights for the operationally-hot categories (Access, Parts, Safety).
const catColour: Record<string, string> = {
  SLA: tokens.indigo,
  Repeat: tokens.indigoLight,
  Access: '#E09935',
  Skill: '#7C6CD8',
  Parts: '#3B82F6',
  Safety: tokens.alert,
  Compliance: '#1F8F6F',
  'Third-party': '#9B59B6',
  Workforce: '#6B7280',
  other: tokens.textSec,
}

export default function Analytics() {
  const [windowDays, setWindowDays] = useState(30)
  const { data: ftfRaw, isLoading: ftfLoading } = useFTFAnalytics(windowDays)
  const { data: trendsRaw, isLoading: trendsLoading } = useTrendsAnalytics(windowDays)

  // Normalise null arrays to [] so downstream .reduce / .map / .length stay
  // safe on a sparse-data tenant. Backend fix in analytics.go init also
  // ships, this is the belt.
  const ftf = useMemo(() => {
    if (!ftfRaw) return ftfRaw
    return {
      ...ftfRaw,
      repeat_premises: ftfRaw.repeat_premises ?? [],
      engineer_ftf: ftfRaw.engineer_ftf ?? [],
      job_type_repeat: ftfRaw.job_type_repeat ?? [],
    }
  }, [ftfRaw])
  const trends = useMemo(() => {
    if (!trendsRaw) return trendsRaw
    return {
      ...trendsRaw,
      daily_volume: trendsRaw.daily_volume ?? [],
      category_breakdown: trendsRaw.category_breakdown ?? [],
      postcode_hotspots: trendsRaw.postcode_hotspots ?? [],
      severity_trend: trendsRaw.severity_trend ?? [],
      hourly_demand: trendsRaw.hourly_demand ?? [],
    }
  }, [trendsRaw])

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Operational analytics
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Window: last {windowDays} days
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <ToggleButtonGroup
              value={windowDays}
              exclusive
              onChange={(_, v) => v && setWindowDays(v)}
              size="small"
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none' } }}
            >
              <ToggleButton value={7}>7 d</ToggleButton>
              <ToggleButton value={30}>30 d</ToggleButton>
              <ToggleButton value={90}>90 d</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {(ftfLoading || trendsLoading) && <LinearProgress />}

          {trends && ftf && (
            <>
              <KpiRow trends={trends} ftfOverall={ftf.summary.overall_ftf_pct} repeatPremises={ftf.summary.repeat_premises_count} />

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 8 }}>
                  <DailyVolumeChart rows={trends.daily_volume} />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <CategoryDonut rows={trends.category_breakdown} />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <SeverityTrendChart rows={trends.severity_trend} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <HourlyHeatmap data={trends.hourly_demand} peakHour={trends.summary.peak_hour_utc} />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 5 }}>
                  <PostcodeHotspotsChart rows={trends.postcode_hotspots} />
                </Grid>
                <Grid size={{ xs: 12, md: 7 }}>
                  <EngineerFtfChart rows={ftf.engineer_ftf} />
                </Grid>
              </Grid>

              <RepeatPremisesTable data={ftf.repeat_premises} />
              <JobTypeRepeatTable data={ftf.job_type_repeat} />
            </>
          )}

          {!ftf && !trends && !ftfLoading && !trendsLoading && (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={24} />
            </Paper>
          )}
        </Stack>
    </Container>
  )
}

// ---------------------------------------------------------------------------
// KPI row
// ---------------------------------------------------------------------------

function KpiRow({
  trends,
  ftfOverall,
  repeatPremises,
}: {
  trends: TrendsAnalytics
  ftfOverall: number
  repeatPremises: number
}) {
  const ftfColour = ftfOverall >= 80 ? '#22C55E' : ftfOverall >= 70 ? '#E09935' : tokens.alert
  return (
    <Grid container spacing={1.5}>
      <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
        <KpiCard
          label="Exceptions raised"
          value={trends.summary.total_exceptions_raised.toLocaleString()}
          footnote={`${trends.summary.total_exceptions_open.toLocaleString()} still open`}
          accent={tokens.indigo}
        />
      </Grid>
      <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
        <KpiCard
          label="Overall FTF"
          value={`${ftfOverall}%`}
          footnote="target ≥ 80%"
          accent={ftfColour}
        />
      </Grid>
      <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
        <KpiCard
          label="Acknowledged"
          value={`${trends.summary.acknowledged_pct}%`}
          footnote="of raised"
          accent={tokens.indigoLight}
        />
      </Grid>
      <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
        <KpiCard
          label="Mean time to ack"
          value={trends.summary.mtta_hours > 0 ? `${trends.summary.mtta_hours} h` : '—'}
          footnote={trends.summary.mtta_hours === 0 ? 'no ack data yet' : 'from opened_at'}
          accent={tokens.gold}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 4, md: 2.4 }}>
        <KpiCard
          label="Repeat premises"
          value={repeatPremises.toLocaleString()}
          footnote={`peak hour ${String(trends.summary.peak_hour_utc).padStart(2, '0')}:00 UTC (${trends.summary.peak_hour_count})`}
          accent={tokens.alert}
        />
      </Grid>
    </Grid>
  )
}

function KpiCard({ label, value, footnote, accent }: { label: string; value: string; footnote?: string; accent: string }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: 2,
        py: 1.5,
        height: '100%',
        borderLeft: `4px solid ${accent}`,
      }}
    >
      <Typography variant="overline" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant="h2" sx={{ fontWeight: 700, lineHeight: 1.1, color: accent }}>
        {value}
      </Typography>
      {footnote && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {footnote}
        </Typography>
      )}
    </Paper>
  )
}

// ---------------------------------------------------------------------------
// Daily volume — multi-series line + bar combo (pure SVG, no chart lib)
// ---------------------------------------------------------------------------

function DailyVolumeChart({ rows }: { rows: DailyVolumeRow[] }) {
  // Layout: ~320 px high, responsive width via viewBox. Bars for total
  // jobs, overlaid line for exceptions_raised.
  const w = 720
  const h = 260
  const padL = 36
  const padR = 36
  const padT = 16
  const padB = 32
  const innerW = w - padL - padR
  const innerH = h - padT - padB

  const maxJobs = Math.max(1, ...rows.map((r) => r.jobs_total))
  const maxEx = Math.max(1, ...rows.map((r) => r.exceptions_raised))

  const xFor = (i: number) => padL + (rows.length === 1 ? innerW / 2 : (innerW / (rows.length - 1)) * i)
  const yEx = (v: number) => padT + innerH - (v / maxEx) * innerH

  const barW = Math.max(6, innerW / Math.max(1, rows.length) - 4)

  const linePath = rows
    .map((r, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i)} ${yEx(r.exceptions_raised)}`)
    .join(' ')

  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <ChartHeader
        title="Daily demand vs. exceptions"
        caption="Bars = jobs scheduled per day (completed/failed split). Line = exceptions raised (right axis). Gap widening = quality slipping."
      />
      <LegendDot colour={tokens.indigo} label="Completed" />{' '}
      <LegendDot colour={tokens.alert} label="Failed" />{' '}
      <LegendDot colour="#E09935" label="Exceptions raised (line)" />

      <Box sx={{ mt: 1, overflowX: 'auto' }}>
        <svg viewBox={`0 0 ${w} ${h}`} width="100%" style={{ maxHeight: 320 }}>
          <line x1={padL} y1={padT + innerH} x2={padL + innerW} y2={padT + innerH} stroke={tokens.divider} />
          {rows.map((r, i) => {
            const x = xFor(i) - barW / 2
            const completedH = innerH * (r.jobs_completed / maxJobs)
            const failedH = innerH * (r.jobs_failed / maxJobs)
            const yCompleted = padT + innerH - completedH
            const yFailed = yCompleted - failedH
            return (
              <g key={r.date}>
                <Tooltip
                  title={`${r.date} · ${r.jobs_total} jobs (${r.jobs_completed} completed, ${r.jobs_failed} failed) · ${r.exceptions_raised} exceptions`}
                >
                  <rect
                    x={x}
                    y={yCompleted}
                    width={barW}
                    height={completedH}
                    fill={tokens.indigo}
                    opacity={0.85}
                  />
                </Tooltip>
                {failedH > 0 && (
                  <rect
                    x={x}
                    y={yFailed}
                    width={barW}
                    height={failedH}
                    fill={tokens.alert}
                    opacity={0.85}
                  />
                )}
                {(i === 0 || i === rows.length - 1 || i === Math.floor(rows.length / 2)) && (
                  <text x={xFor(i)} y={h - 8} fontSize="10" textAnchor="middle" fill={tokens.textSec}>
                    {shortDate(r.date)}
                  </text>
                )}
              </g>
            )
          })}
          <path d={linePath} stroke="#E09935" strokeWidth={2} fill="none" />
          {rows.map((r, i) => (
            <circle key={`p-${r.date}`} cx={xFor(i)} cy={yEx(r.exceptions_raised)} r={3} fill="#E09935" />
          ))}

          {/* Left axis label */}
          <text x={padL - 4} y={padT + 6} fontSize="10" fill={tokens.textSec} textAnchor="end">
            {maxJobs}
          </text>
          <text x={padL - 4} y={padT + innerH} fontSize="10" fill={tokens.textSec} textAnchor="end">
            0
          </text>
          {/* Right axis label */}
          <text x={padL + innerW + 4} y={padT + 6} fontSize="10" fill="#E09935" textAnchor="start">
            {maxEx}
          </text>
          <text x={padL + innerW + 4} y={padT + innerH} fontSize="10" fill="#E09935" textAnchor="start">
            0
          </text>
        </svg>
      </Box>
    </Paper>
  )
}

// ---------------------------------------------------------------------------
// Category donut — open exceptions split by category
// ---------------------------------------------------------------------------

function CategoryDonut({ rows }: { rows: CategoryRow[] }) {
  const total = rows.reduce((s, r) => s + r.total, 0)
  const size = 200
  const stroke = 34
  const r = size / 2 - stroke / 2
  const cx = size / 2
  const cy = size / 2
  const circ = 2 * Math.PI * r

  let offset = 0
  const segments = rows.map((row) => {
    const frac = total > 0 ? row.total / total : 0
    const len = frac * circ
    const seg = (
      <circle
        key={row.category}
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke={catColour[row.category] ?? tokens.textSec}
        strokeWidth={stroke}
        strokeDasharray={`${len} ${circ}`}
        strokeDashoffset={-offset}
        transform={`rotate(-90 ${cx} ${cy})`}
      />
    )
    offset += len
    return seg
  })

  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <ChartHeader title="Open by category" caption="Where the pain is right now. Click-free — hover a slice for totals." />
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mt: 1 }}>
        <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={tokens.divider} strokeWidth={stroke} />
          {segments}
          <text x={cx} y={cy - 2} textAnchor="middle" fontSize="22" fontWeight={700} fill={tokens.charcoal}>
            {total.toLocaleString()}
          </text>
          <text x={cx} y={cy + 16} textAnchor="middle" fontSize="10" fill={tokens.textSec}>
            open
          </text>
        </svg>
        <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
          {rows.map((row) => (
            <Stack key={row.category} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: catColour[row.category] ?? tokens.textSec, flexShrink: 0 }} />
              <Typography variant="body2" sx={{ flexGrow: 1 }} noWrap>
                {row.category}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 32, textAlign: 'right' }}>
                {row.total}
              </Typography>
              {row.critical > 0 && (
                <Typography variant="caption" sx={{ color: sevColour.critical, fontWeight: 700, minWidth: 24, textAlign: 'right' }}>
                  {row.critical}!
                </Typography>
              )}
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Paper>
  )
}

// ---------------------------------------------------------------------------
// Severity trend — stacked area (critical on top, warn at bottom)
// ---------------------------------------------------------------------------

function SeverityTrendChart({ rows }: { rows: SeverityTrendRow[] }) {
  const w = 520
  const h = 220
  const padL = 32
  const padR = 12
  const padT = 16
  const padB = 28
  const innerW = w - padL - padR
  const innerH = h - padT - padB

  const totals = rows.map((r) => r.critical + r.high + r.warn + r.info)
  const max = Math.max(1, ...totals)
  const xFor = (i: number) => padL + (rows.length === 1 ? innerW / 2 : (innerW / (rows.length - 1)) * i)

  // Build stacked cumulative path per series (bottom → top).
  const seriesOrder: Array<keyof Pick<SeverityTrendRow, 'warn' | 'high' | 'critical'>> = ['warn', 'high', 'critical']

  const cum = rows.map(() => 0)
  const paths = seriesOrder.map((key) => {
    const top = rows.map((r, i) => {
      cum[i] += r[key]
      return { x: xFor(i), y: padT + innerH - (cum[i] / max) * innerH }
    })
    // Build area: top line forward + baseline back
    const forward = top.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
    const bottom = top
      .slice()
      .reverse()
      .map((p, i) => {
        const revIdx = top.length - 1 - i
        const below = cum[revIdx] - (rows[revIdx][key] ?? 0)
        const y = padT + innerH - (below / max) * innerH
        return `L ${p.x} ${y}`
      })
      .join(' ')
    return {
      key,
      d: `${forward} ${bottom} Z`,
      colour: sevColour[key],
    }
  })

  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <ChartHeader title="Severity mix over time" caption="Stacked: warn · high · critical. Rising warn with flat critical = warning system working." />
      <Stack direction="row" spacing={1.5} sx={{ mb: 1 }}>
        <LegendDot colour={sevColour.critical} label="Critical" />
        <LegendDot colour={sevColour.high} label="High" />
        <LegendDot colour={sevColour.warn} label="Warn" />
      </Stack>
      <Box sx={{ overflowX: 'auto' }}>
        <svg viewBox={`0 0 ${w} ${h}`} width="100%" style={{ maxHeight: 260 }}>
          <line x1={padL} y1={padT + innerH} x2={padL + innerW} y2={padT + innerH} stroke={tokens.divider} />
          {paths.map((p) => (
            <path key={p.key} d={p.d} fill={p.colour} opacity={0.75} />
          ))}
          <text x={padL - 4} y={padT + 6} fontSize="10" fill={tokens.textSec} textAnchor="end">
            {max}
          </text>
          <text x={padL - 4} y={padT + innerH} fontSize="10" fill={tokens.textSec} textAnchor="end">
            0
          </text>
          {rows.length > 2 && (
            <>
              <text x={xFor(0)} y={h - 8} fontSize="10" fill={tokens.textSec} textAnchor="middle">
                {shortDate(rows[0].date)}
              </text>
              <text x={xFor(rows.length - 1)} y={h - 8} fontSize="10" fill={tokens.textSec} textAnchor="middle">
                {shortDate(rows[rows.length - 1].date)}
              </text>
            </>
          )}
        </svg>
      </Box>
    </Paper>
  )
}

// ---------------------------------------------------------------------------
// Hourly heatmap — 24 bars (UTC hour), peak hour highlighted.
// ---------------------------------------------------------------------------

function HourlyHeatmap({ data, peakHour }: { data: number[]; peakHour: number }) {
  const max = Math.max(1, ...data)
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <ChartHeader
        title="Exceptions by hour of day (UTC)"
        caption={`Peak: ${String(peakHour).padStart(2, '0')}:00 UTC. Use this to align rota breaks / ops-desk staffing.`}
      />
      <Stack direction="row" spacing={0.5} sx={{ mt: 1, alignItems: 'flex-end', height: 140 }}>
        {data.map((v, hour) => {
          const frac = v / max
          const isPeak = hour === peakHour
          return (
            <Tooltip key={hour} title={`${String(hour).padStart(2, '0')}:00 · ${v} exceptions`}>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 8,
                  height: `${Math.max(4, frac * 100)}%`,
                  bgcolor: isPeak ? tokens.alert : v === 0 ? tokens.divider : tokens.indigo,
                  opacity: isPeak ? 1 : 0.45 + frac * 0.55,
                  borderRadius: 0.5,
                  transition: 'background-color 120ms, opacity 120ms',
                }}
              />
            </Tooltip>
          )
        })}
      </Stack>
      <Stack direction="row" sx={{ mt: 0.5, justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>00:00</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>06:00</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>12:00</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>18:00</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>23:00</Typography>
      </Stack>
    </Paper>
  )
}

// ---------------------------------------------------------------------------
// Postcode hotspots — horizontal stacked bars
// ---------------------------------------------------------------------------

function PostcodeHotspotsChart({ rows }: { rows: PostcodeHotspotRow[] }) {
  const max = Math.max(1, ...rows.map((r) => r.open_count))
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <ChartHeader title="Postcode hotspots" caption="Where the open exceptions are concentrated. Pair with the Map view for a geo read." />
      <Stack spacing={0.75} sx={{ mt: 1 }}>
        {rows.length === 0 && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No exceptions with a postcode attached.
          </Typography>
        )}
        {rows.map((r) => {
          const widthPct = (r.open_count / max) * 100
          return (
            <Box key={r.postcode}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.25 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, width: 56 }}>
                  {r.postcode}
                </Typography>
                <Box sx={{ flex: 1, height: 14, bgcolor: tokens.divider, borderRadius: 0.5, overflow: 'hidden' }}>
                  <Box sx={{ display: 'flex', height: '100%', width: `${widthPct}%` }}>
                    {r.critical > 0 && <Box sx={{ flex: r.critical, bgcolor: sevColour.critical }} />}
                    {r.high > 0 && <Box sx={{ flex: r.high, bgcolor: sevColour.high }} />}
                    {r.warn > 0 && <Box sx={{ flex: r.warn, bgcolor: sevColour.warn }} />}
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 36, textAlign: 'right' }}>
                  {r.open_count}
                </Typography>
              </Stack>
              {(r.critical > 0 || r.high > 0) && (
                <Stack direction="row" spacing={1.5} sx={{ pl: 7 }}>
                  {r.critical > 0 && <Typography variant="caption" sx={{ color: sevColour.critical, fontWeight: 600 }}>{r.critical} critical</Typography>}
                  {r.high > 0 && <Typography variant="caption" sx={{ color: sevColour.high, fontWeight: 600 }}>{r.high} high</Typography>}
                </Stack>
              )}
            </Box>
          )
        })}
      </Stack>
    </Paper>
  )
}

// ---------------------------------------------------------------------------
// Engineer FTF — horizontal bar for each engineer with FTF %
// ---------------------------------------------------------------------------

type EngineerFTFLocalRow = {
  engineer_id: string
  display_name: string
  external_ref: string
  completed: number
  failed: number
  total_closed: number
  ftf_pct: number
}

function EngineerFtfChart({ rows }: { rows: EngineerFTFLocalRow[] }) {
  const sorted = useMemo(() => [...rows].sort((a, b) => a.ftf_pct - b.ftf_pct), [rows])
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <ChartHeader
        title="Engineer FTF — lowest first"
        caption="These are the 1:1 / training / patch-review conversations that move the dial. Target zone = 80%+ (green)."
      />
      {sorted.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Not enough closed jobs per engineer in this window to compute FTF.
        </Typography>
      ) : (
        <Stack spacing={0.75} sx={{ mt: 1 }}>
          {sorted.map((r) => {
            const colour = r.ftf_pct >= 80 ? '#22C55E' : r.ftf_pct >= 70 ? '#E09935' : tokens.alert
            return (
              <Stack key={r.engineer_id} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Box sx={{ width: 140, minWidth: 140 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                    {r.display_name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {r.external_ref} · {r.total_closed} jobs
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, height: 14, bgcolor: tokens.divider, borderRadius: 0.5, overflow: 'hidden' }}>
                  <Box sx={{ width: `${r.ftf_pct}%`, height: '100%', bgcolor: colour, transition: 'width 200ms ease' }} />
                </Box>
                <Chip
                  label={`${r.ftf_pct}%`}
                  size="small"
                  sx={{ minWidth: 56, height: 22, bgcolor: `${colour}22`, color: colour, fontWeight: 700 }}
                />
              </Stack>
            )
          })}
        </Stack>
      )}
    </Paper>
  )
}

// ---------------------------------------------------------------------------
// Tables that complement the charts (kept from original page)
// ---------------------------------------------------------------------------

function RepeatPremisesTable({
  data,
}: {
  data: Array<{ premises_id: string; address_line_1: string; postcode: string; visits: number; distinct_job_types: number }>
}) {
  return (
    <Paper variant="outlined">
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Top repeat-visit premises
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          ≥2 jobs inside the window. These are the customer accounts masking the true FTF picture.
        </Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Address</TableCell>
            <TableCell>Postcode</TableCell>
            <TableCell align="right">Visits</TableCell>
            <TableCell align="right">Distinct job types</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} sx={{ color: 'text.secondary' }}>
                No repeat visits in window.
              </TableCell>
            </TableRow>
          )}
          {data.map((r) => (
            <TableRow key={r.premises_id} hover>
              <TableCell>{r.address_line_1}</TableCell>
              <TableCell>{r.postcode}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                {r.visits}
              </TableCell>
              <TableCell align="right">{r.distinct_job_types}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  )
}

function JobTypeRepeatTable({
  data,
}: {
  data: Array<{ job_type_id: string; code: string; display_name: string; total_jobs: number; unique_premises: number; repeat_pct: number }>
}) {
  return (
    <Paper variant="outlined">
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Job-type repeat rate
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          High repeat % = same customers keep needing the same job. Diagnostic or training gap.
        </Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Code</TableCell>
            <TableCell>Display name</TableCell>
            <TableCell align="right">Total jobs</TableCell>
            <TableCell align="right">Unique premises</TableCell>
            <TableCell align="right">Repeat %</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((r) => (
            <TableRow key={r.job_type_id} hover>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.code}</TableCell>
              <TableCell>{r.display_name}</TableCell>
              <TableCell align="right">{r.total_jobs}</TableCell>
              <TableCell align="right">{r.unique_premises}</TableCell>
              <TableCell align="right">
                <Chip
                  label={`${r.repeat_pct}%`}
                  size="small"
                  color={r.repeat_pct > 20 ? 'error' : r.repeat_pct > 10 ? 'warning' : 'default'}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  )
}

// ---------------------------------------------------------------------------
// Small shared bits
// ---------------------------------------------------------------------------

function ChartHeader({ title, caption }: { title: string; caption: string }) {
  return (
    <>
      <Typography variant="h5" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
        {caption}
      </Typography>
    </>
  )
}

function LegendDot({ colour, label }: { colour: string; label: string }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colour }} />
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
    </Box>
  )
}

function shortDate(iso: string): string {
  // iso = 'YYYY-MM-DD' — return 'DD MMM' (en-GB compact).
  const d = new Date(iso + 'T00:00:00Z')
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' })
}
